import { IntlShape } from 'react-intl';

export const PREDICT_PREVIEW_VIDEO = 'https://futrli.wistia.com/medias/i1wkydtlvs';

/* eslint-disable no-magic-numbers */

export enum DATE_PERIODS {
  WEEKLY = 'weekly',
  MONTHLY = 'monthly'
}

export enum WEEKLY_OFFSET {
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY
}

export type PeriodType = 'MONTHLY' | 'WEEKLY_MONDAY' | 'WEEKLY_SUNDAY';

export const PERIOD_TYPE_QUERY_PARAM = 'period-type';

export function isPeriodType(periodType: unknown): periodType is PeriodType {
  if (typeof periodType !== 'string') {
    return false;
  }
  return periodType in PERIOD_TYPE_OPTIONS;
}

type PeriodTypeOptions = {
  [key in PeriodType]: PeriodType;
};

export const PERIOD_TYPE_OPTIONS: PeriodTypeOptions = {
  MONTHLY: 'MONTHLY',
  WEEKLY_MONDAY: 'WEEKLY_MONDAY',
  WEEKLY_SUNDAY: 'WEEKLY_SUNDAY'
};

export const DAYS_TO_END_OF_WEEK = 6;

type PeriodRequestData = {
  [key in PeriodType]: {
    period: DATE_PERIODS;
    offset: string;
  };
};

export const PERIOD_TYPE_REQUEST_DATA: PeriodRequestData = {
  MONTHLY: { period: DATE_PERIODS.MONTHLY, offset: '' },
  WEEKLY_MONDAY: { period: DATE_PERIODS.WEEKLY, offset: WEEKLY_OFFSET.MONDAY.toString() },
  WEEKLY_SUNDAY: { period: DATE_PERIODS.WEEKLY, offset: WEEKLY_OFFSET.SUNDAY.toString() }
};

export enum ACCOUNTS {
  TOTAL = 'total',
  ACCOUNT = 'account'
}

export const SYSTEM_CALC = 'SystemCalc';
export const SHEETS_ASSUMPTION = 'SheetsAssumption';

export enum ASSUMPTION_TYPES {
  AI = 'AiAssumption',
  BASIC = 'BasicAssumption',
  UNITS = 'UnitAssumption',
  REPEATING = 'RepeatingAssumption',
  SUBSCRIPTION = 'SubscriptionAssumption',
  FORMULA = 'FormulaAssumption'
}

export const ASSUMPTION_URL_NAME: Record<string, ASSUMPTION_TYPES> = {
  repeating: ASSUMPTION_TYPES.REPEATING,
  unit: ASSUMPTION_TYPES.UNITS,
  basic: ASSUMPTION_TYPES.BASIC,
  formula: ASSUMPTION_TYPES.FORMULA
};

export type AssumptionTypesUnion = `${ASSUMPTION_TYPES}`;
export const AssumptionTypes: AssumptionTypesUnion[] = [
  ASSUMPTION_TYPES.AI,
  ASSUMPTION_TYPES.BASIC,
  ASSUMPTION_TYPES.UNITS,
  ASSUMPTION_TYPES.REPEATING,
  ASSUMPTION_TYPES.SUBSCRIPTION,
  ASSUMPTION_TYPES.FORMULA
];

export const ASSUMPTION_TYPES_URL_PARAMS = {
  [ASSUMPTION_TYPES.AI]: 'ai',
  [ASSUMPTION_TYPES.BASIC]: 'basic',
  [ASSUMPTION_TYPES.UNITS]: 'unit',
  [ASSUMPTION_TYPES.REPEATING]: 'repeating',
  [ASSUMPTION_TYPES.SUBSCRIPTION]: 'subscription',
  [ASSUMPTION_TYPES.FORMULA]: 'formula'
};

export const ASSUMPTION_TYPE_NAMES = (intl: IntlShape) => ({
  [ASSUMPTION_TYPES.AI]: intl.formatMessage({ id: 'assumption_type.ai', defaultMessage: 'ai' }),
  [ASSUMPTION_TYPES.BASIC]: intl
    .formatMessage({ id: 'predict.freestyle', defaultMessage: 'Freestyle' })
    .toLocaleLowerCase(),
  [ASSUMPTION_TYPES.UNITS]: intl.formatMessage({ id: 'common.unit', defaultMessage: 'Unit' }).toLocaleLowerCase(),
  [ASSUMPTION_TYPES.REPEATING]: intl
    .formatMessage({ id: 'common.repeating', defaultMessage: 'Repeating' })
    .toLocaleLowerCase(),
  [ASSUMPTION_TYPES.SUBSCRIPTION]: intl.formatMessage({
    id: 'common.subscription.subscription',
    defaultMessage: 'subscription'
  }),
  [ASSUMPTION_TYPES.FORMULA]: intl
    .formatMessage({ id: 'common.formula', defaultMessage: 'Formula' })
    .toLocaleLowerCase()
});

export const INITIAL_ROW_HEADER_WIDTH = 298;

export enum SIZES {
  X_SMALL = 'x-small',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  X_LARGE = 'x-large'
}

export enum FORECAST_KIND {
  DEFAULT = '',
  PREDICTIONS = 'predictions',
  PAYMENTS = 'payments'
}

export enum FINANCIAL_DETAIL_KIND {
  DEFAULT = 'profit-and-loss',
  PROFIT_AND_LOSS = 'profit-and-loss',
  BALANCE_SHEET = 'balance-sheet',
  CASH_FLOW_STATEMENT = 'cash-flow-statement'
}

export enum INVOICE_DETAIL_KIND {
  DEFAULT = 'invoice',
  INVOICE = 'invoice',
  BILL = 'bill'
}

export const FINANCIAL_PAGES_WITHOUT_PAYMENTS: string[] = [
  FINANCIAL_DETAIL_KIND.BALANCE_SHEET,
  FINANCIAL_DETAIL_KIND.CASH_FLOW_STATEMENT
];

export const ASSUMPTION_DATE_FORMAT = 'yyyy-MM-dd';

export enum KEY_CODES {
  TAB = 9,
  ENTER = 13,
  ESCAPE = 27
}

export enum PAYMENT_TYPES {
  AI_DAYS = 'AiDaysPaymentType',
  SINGLE = 'SinglePaymentType',
  BASIC = 'BasicPaymentType',
  NON_CASH = 'NonCashPaymentType'
}

export const PAYMENT_TYPES_URL_PARAMS = {
  [PAYMENT_TYPES.AI_DAYS]: 'ai-days',
  [PAYMENT_TYPES.SINGLE]: 'single',
  [PAYMENT_TYPES.BASIC]: 'basic',
  [PAYMENT_TYPES.NON_CASH]: 'non-cash'
};

export const CHART_START = 3;
export const CHART_END = 15;

export const BLACKLIST_NUMBER_VALUES = ['-', '.', '-.', '-0', '0', '-0.', '0.', '-0.0', '0.0', '-0.00', '0.00'];

export const MAX_PERCENTAGE = 100;

export const MONTHS_IN_YEAR = 12;

export const DEFAULT_VAT_RATE = 20;
export const DEFAULT_VAT_PAYMENT_DAYS = 45;
export const DEFAULT_VAT_SCHEME = 'accrual';
export const QUARTERLY_MONTH = 3;

export enum VAT_PERIOD {
  MONTHLY = 'monthly',
  TWO_MONTHLY = 'two_monthly',
  QUARTERLY = 'quarterly',
  SIX_MONTHLY = 'six_monthly',
  ANNUALLY = 'yearly'
}

export const MAX_VAT_PAYMENT_DAYS_MONTHLY = 28;

export enum VAT_SCHEME {
  ACCRUAL_BASED = 'accrual',
  CASH_BASED = 'cash'
}

export enum PREDICTION_ACTION {
  CONFIGURE = 'CONFIGURE',
  RENAME = 'RENAME',
  DELETE = 'DELETE',
  CHANGE_AMOUNTS = 'CHANGE_AMOUNTS',
  CHANGE_AMOUNTS_AND_SWITCH_TO_NET = 'CHANGE_AMOUNTS_AND_SWITCH_TO_NET',
  QUICK_COPY = 'QUICK_COPY'
}

export enum GROUPING_ACTION {
  RENAME = 'RENAME',
  DELETE = 'DELETE'
}

export const EXPENSE_CANONICAL_CATEGORIES = [
  'expenses',
  'other_expenses',
  'cost_of_sales',
  'current_liabilities',
  'non_current_liabilities'
];

export enum ACCOUNT_TITLE {
  COST_OF_SALES = 'cost of sales',
  EXPENSES = 'expense',
  SALES = 'sales'
}

export enum INVOICE_STATUS_TYPES {
  ASSUMED_TO_BE_PAID_TODAY = 'Assumed to be paid today',
  MANUALLY_EXCLUDED = 'Manually excluded'
}

export const LAST_DAY_OF_MONTH = 'last_day_of_month';

export const LAST_DAY_OF_MONTH_NUM = 31;

export const MIN_PREV_YEARS_FOR_ASSUMPTIONS = 1;
export const MAX_FUTURE_YEARS_FOR_ASSUMPTIONS = 3;

export enum REPORT_SECTION {
  ProfitLoss = 'profit_loss',
  BalanceSheet = 'balance_sheet',
  CashFlow = 'cash_flow'
}

export enum CASH_FLOW_MAIN_CATEGORIES {
  StartingBalance = 'starting_balance',
  ClosingBalance = 'closing_balance'
}

export const TRACKING_EVENTS = {
  // Existing events
  TOGGLED_ON_CASH_FLOW_VIEW: 'Toggled on cash flow view',
  CLICKED_EXPLAIN_PREDICTION: 'Clicked explain prediction',
  CHANGE_INVOICE_BILL_DATE: 'Changed the expected payment date of an invoice or bill',
  EXCLUDE_BILL: 'Excluded a bill',
  CHANGE_BILL_DATE: 'Changed the expected payment date of a bill',
  EXPANDED_ACCOUNT_LINE: 'Expanded an account line',
  EXPANDED_GROUP_LINE: 'Expanded a group line',
  CREATE_PREDICTION_FREESTYLE: 'Created a Freestyle Prediction',
  CREATE_PREDICTION_REPEATING: 'Created a Repeating Prediction',
  CREATE_PREDICTION_UNIT: 'Created a Unit Prediction',
  CREATE_PREDICTION_FORMULA: 'Created a Formula Prediction',
  CLICKED_SETTINGS: 'Clicked "Settings" in Predict',
  SAVED_DEFERRED_VAT: 'Saved Deferred Vat',
  EXPORT_REPORT: 'Exporting report',
  EXPORT_VIEW_REPORT: 'View report export',
  EXPORT_AUTHORISE: 'Authorising report export',
  IMPORT_AUTHORISE: 'Authorising sheets import',
  IMPORT_CREATE: 'Created import sheet',
  IMPORT_DELETE: 'Deleted import sheet',
  IMPORT_SYNC_SUCCESS: 'Success importing sheet predictions',
  IMPORT_SYNC_FAILURE: 'Failure importing sheet predictions',
  IMPORT_OPEN_TEMPLATE: 'Open import sheet template',
  // New events
  SET_UP_INSIGHTS_EMAIL_HOME_PAGE: 'Set up insights email (home page)',
  CREATED_ANY_MANUAL_PREDICTION: 'Created any manual prediction',
  CREATE_PREDICTION_FREESTYLE_NEW: 'Created Freestyle prediction',
  CREATE_PREDICTION_REPEATING_NEW: 'Created Repeating prediction',
  CREATE_PREDICTION_UNIT_NEW: 'Created Unit prediction',
  CREATE_PREDICTION_FORMULA_NEW: 'Created Formula prediction',
  CREATED_ANY_FORECAST: 'Created any forecast',
  CREATED_1_YR_P_L: 'Created 1 yr Profit & loss',
  CREATED_3_YR_CASH_FLOW: 'Created 3 yr cash flow',
  CREATED_3_YR_CASH_FLOW_INVOICES: 'Created 3 yr cash flow with invoices',
  CREATE_FREESTYLE_DRIVER: 'Created Freestyle Driver',
  CREATE_FORMULA_DRIVER: 'Created Formula Driver',
  CREATE_ANY_REPORT_OR_DASHBOARD: 'Created any report or dashboard',
  CREATE_LANDSCAPE_REPORT: 'Created Landscape report',
  CREATE_PORTRAIT_REPORT: 'Created Portrait report',
  CREATE_DASHBOARD: 'Created Dashboard',
  ADDED_TEMPLATE_TO_REPORT: 'Added Template to report (side panel in reporting)',
  ADDED_WIDGET_TO_REPORT: 'Added Widget to report',
  ADDED_CHART_TO_REPORT: 'Added Chart to report',
  ADDED_TABLE_TO_REPORT: 'Added Table to report',
  ADDED_DATA_CARD_TO_REPORT: 'Added Data card to report',
  ADDED_TEXT_TO_REPORT: 'Added Text to report',
  ADDED_IMAGE_TO_REPORT: 'Added Image to report',
  CREATE_SCHEDULE: 'Created Schedule',
  CLICKED_CUSTOMER_SUPPLIER_DETAIL: 'Clicked customer/supplier detail',
  SENT_INSTANT_PDF: 'Sent instant PDF',
  CREATED_ROW_FORMULA: 'Created Row formula',
  TOGGLED_PREDICTION_ON_OFF: 'Toggled prediction on/off',
  EDITED_INVOICE: 'Edited invoice',
  BULK_ACTIONED_AUTO_PREDICTIONS: 'Bulk actioned auto-predictions',
  BULK_ACTIONED_INVOICES: 'Bulk actioned invoices',
  VIEWED_DAILY_TRACKER: 'Viewed Daily Tracker',
  EXPANDED_ACCOUNT: 'Expanded account',
  USED_GOOGLE_SHEETS_IMPORT: 'Used Google Sheets import',
  OPENED_ASSISTANT: 'Opened Assistant',
  SAVED_FORECAST_SETTINGS: 'Saved forecast settings',
  EXPORTED_REPORT: 'Exported forecast to GSheets',
  INVITED_USER: 'Invited user',
  ACCEPTED_INVITE: 'Accepted invite',
  ADDED_XERO_ORGANISATION: 'Added Xero organisation',
  ADDED_QBO_ORGANISATION: 'Added QBO organisation',
  ADDED_SAGE_ORGANISATION: 'Added SBCA organisation',
  ADDED_EXCEL_ORGANISATION: 'Added Excel organisation',
  ADDED_DEMO_UK_ORGANISATION: 'Added Demo UK organisation',
  ADDED_DEMO_AU_ORGANISATION: 'Added Demo AU organisation',
  STARTED_TRIAL_DIRECT: 'Started Trial - Direct',
  VIEWED_GO_PRO_MODAL: 'Viewed go-pro modal',
  PURCHASED_SUBSCRIPTION_DIRECT: 'Purchased Subscription - Direct',
  LOGGED_IN: 'Logged in',
  RECLASSIFIED_ACCOUNTS_COA: 'Reclassified accounts (CoA)',
  USED_SWITCH_RULE_COA: 'Used Switch rule (CoA)',
  CREATED_ACCOUNT_GROUP_COA: 'Created account group (CoA)',
  // consolidation
  ADD_PREDICTION_FORMULA_CONSOLIDATION: 'Add prediction/formula consolidation',
  // ADD_IMPORT_SPREADSHEET_CONSOLIDATION: 'Add import spreadsheet consolidation',
  CREATE_CONSOLIDATION_FORECAST: 'Create consolidation forecast',
  CREATE_CONSOLIDATION_REPORT: 'Create consolidation report',
  CREATE_CONSOLIDATION_SCHEDULE: 'Create consolidation schedule',
  CREATE_CONSOLIDATION_PDF_AND_SEND: 'Create consolidation PDF & send',
  OPEN_STANDARD_ORG_MANUAL_PREDICTION_SIDEBAR: 'Opened standard org manual prediction sidebar',
  OPEN_CONSOLIDATED_ORG_MANUAL_PREDICTION_SIDEBAR: 'Opened consolidated org manual prediction sidebar'
};

export enum CATEGORY_NAME_INDEXES {
  income = 'income',
  other_income = 'other_income',
  expenses = 'expenses',
  other_expenses = 'other_expenses',
  cost_of_sales = 'cost_of_sales',
  bank_accounts = 'bank_accounts',
  current_assets = 'current_assets',
  non_current_assets = 'non_current_assets',
  fixed_assets = 'fixed_assets',
  current_liabilities = 'current_liabilities',
  non_current_liabilities = 'non_current_liabilities',
  equity = 'equity'
}

export const CATEGORY_NAME_FINANCIALS = 'financials';

export enum QUERY_PARAMS {
  date = 'date',
  reportSection = 'report_section',
  rowIndex = 'row_index',
  rowType = 'row_type',
  period = 'period'
}

export const CURRENCY_DECIMAL_PLACES = 2;
export const MAX_DIGITS_IN_CELL = 8;

export const MAX_DIGITS_IN_CURRENCY_FIELD = 3;

export const CELL_WIDTH = 96;
